import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './safety-report-viewer.style.js';
import { SafetyReportType, SafetyReportStatus } from '@cmms/models';

@customElement('safety-report-viewer')
export class SafetyReportViewer extends LitElement {
  static styles = styles;

  @property({ type: Object }) report: any = null;
  @state() private safetyActions: any[] = [];

  connectedCallback() {
    super.connectedCallback();
    if (this.report) {
      this.loadSafetyActions();
    }
  }

  private async loadSafetyActions() {
    try {
      // TODO: Replace with actual API call
      // const response = await getActionsByReportId(db, this.report.id);
      // this.safetyActions = response.data || [];
      
      // Mock data for now
      this.safetyActions = [
        {
          id: 'sa_1',
          action_title: 'Review and update safety procedures',
          action_description: 'Review current safety procedures and identify areas for improvement.',
          status: 'todo',
          due_date: Date.now() + 86400000,
          assigned_to_user_id: 'user_1'
        },
        {
          id: 'sa_2',
          action_title: 'Conduct safety training session',
          action_description: 'Conduct comprehensive safety training for all affected personnel.',
          status: 'in_progress',
          due_date: Date.now() + 172800000,
          assigned_to_user_id: 'user_2'
        }
      ];
    } catch (err) {
      console.error('Error loading safety actions:', err);
    }
  }

  private getStatusBadgeVariant(status: string) {
    switch (status) {
      case SafetyReportStatus.OPEN:
        return 'warning';
      case SafetyReportStatus.ASSIGNED:
        return 'info';
      case SafetyReportStatus.INVESTIGATING:
        return 'primary';
      case SafetyReportStatus.RESOLVED:
        return 'success';
      default:
        return 'neutral';
    }
  }

  private getTypeBadgeVariant(type: string) {
    switch (type) {
      case SafetyReportType.SAFETY_OBSERVATION:
        return 'warning';
      case SafetyReportType.NEAR_MISS:
        return 'info';
      case SafetyReportType.INCIDENT:
        return 'danger';
      default:
        return 'neutral';
    }
  }

  private formatDate(timestamp: number) {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private closeModal() {
    const dialog = this.shadowRoot?.querySelector('sl-dialog') as any;
    dialog?.hide();
  }

  private editReport() {
    // Close viewer and open editor
    this.closeModal();
    const modal = document.createElement('safety-report-modal') as any;
    modal.report = this.report;
    document.body.appendChild(modal);
  }

  render() {
    if (!this.report) {
      return html`<sl-dialog label="Safety Report" @sl-hide="${() => this.remove()}"></sl-dialog>`;
    }

    return html`
      <sl-dialog
        label="Safety Report Details"
        @sl-hide="${() => this.remove()}"
      >
        <div class="report-header">
          <h2 class="report-title">${this.report.title}</h2>
          <div class="actions">
            <sl-button size="small" variant="text" @click="${this.editReport}">
              <sl-icon name="pencil"></sl-icon>
            </sl-button>
          </div>
        </div>

        <div class="report-meta">
          <sl-badge variant="${this.getTypeBadgeVariant(this.report.report_type)}" class="type-badge">
            ${this.report.report_type.replace('_', ' ').toUpperCase()}
          </sl-badge>
          <sl-badge variant="${this.getStatusBadgeVariant(this.report.status)}" class="status-badge">
            ${this.report.status.toUpperCase()}
          </sl-badge>
        </div>

        <div class="report-details">
          <div class="detail-group">
            <div class="detail-label">Date Reported</div>
            <div class="detail-value">${this.formatDate(this.report.date_reported)}</div>
          </div>

          <div class="detail-group">
            <div class="detail-label">Location</div>
            <div class="detail-value">${this.report.location_id || 'Unknown Location'}</div>
          </div>

          <div class="detail-group">
            <div class="detail-label">Reported By</div>
            <div class="detail-value">User ID: ${this.report.reported_by_user_id}</div>
          </div>

          <div class="detail-group">
            <div class="detail-label">Associated Asset</div>
            <div class="detail-value">${this.report.associated_asset_id || 'None'}</div>
          </div>
        </div>

        <div class="description-section">
          <div class="detail-label">Description</div>
          <div class="description-content">
            ${this.report.description}
          </div>
        </div>

        ${this.report.attachments && this.report.attachments.length > 0 ? html`
          <div class="attachments-section">
            <div class="detail-label">Attachments</div>
            <div class="attachment-list">
              ${this.report.attachments.map((attachment: any) => html`
                <div class="attachment-item">
                  <sl-icon name="paperclip"></sl-icon>
                  <span>${attachment.storage_path}</span>
                  <sl-badge variant="neutral" size="small">
                    ${Math.round(attachment.size / 1024)} KB
                  </sl-badge>
                </div>
              `)}
            </div>
          </div>
        ` : ''}

        ${this.safetyActions.length > 0 ? html`
          <sl-divider></sl-divider>
          <div>
            <div class="detail-label">Related Safety Actions</div>
            <div class="attachment-list">
              ${this.safetyActions.map((action: any) => html`
                <div class="attachment-item">
                  <sl-icon name="list"></sl-icon>
                  <span>${action.action_title}</span>
                  <sl-badge variant="neutral" size="small">
                    ${action.status.toUpperCase()}
                  </sl-badge>
                </div>
              `)}
            </div>
          </div>
        ` : ''}

        <div slot="footer" class="actions">
          <sl-button @click="${this.closeModal}">Close</sl-button>
        </div>
      </sl-dialog>
    `;
  }
}
